import { Play, RefreshCw, Save, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import LoadingSpinner from '../../../../components/LoadingSpinner';
import { DataTable } from '../../../../components/DataTable';
import type { Column } from '../../../../components/DataTable';
import {
  CRON_WEEKDAYS,
  describeCronSchedule,
  EXPECTED_CRON_JOBS,
  fetchCronJobRuns,
  fetchCronJobs,
  fetchMergedExecutionHistory,
  beginCronInvoke,
  fetchCronEdgeRuns,
  formatDuration,
  formatRunDays,
  formatTimestamp,
  invokeCronJobDirect,
  invokePeerlistIngest,
  pollCronInvoke,
  runNowResultHint,
  statusChipClass,
  updateCronJobSettings,
  type CronJobRow,
  type CronJobRunRow,
} from '../../../../services/cronJobsService';

async function resolveEdgeRunMessage(
  requestId: number,
  hint: string,
): Promise<{ ok: boolean; msg?: string; err?: string } | null> {
  await new Promise((r) => window.setTimeout(r, 3500));
  const runs = await fetchCronEdgeRuns(30).catch(() => []);
  const run = runs.find((r) => r.request_id === requestId);
  if (!run?.completed_at && !run?.http_status) return null;

  const preview = (run.response_preview ?? run.error_message ?? '').toString().slice(0, 200);
  const httpStatus = run.http_status ?? (run.ok ? 200 : null);

  if (run.ok) {
    return {
      ok: true,
      msg: `✓ Completed — HTTP ${httpStatus ?? 200}.${preview ? ` ${preview}` : ''} ${hint}`,
    };
  }

  return {
    ok: false,
    err:
      `Job ran but reported a problem — HTTP ${httpStatus ?? '—'}`
      + (preview ? ` · ${preview}` : '')
      + '. See Execution History for details.',
  };
}

type CronSubview = 'jobs' | 'history';

const CRON_VIEW_PARAM = 'cronView';

function readCronViewFromUrl(): CronSubview {
  if (typeof window === 'undefined') return 'jobs';
  return new URLSearchParams(window.location.search).get(CRON_VIEW_PARAM) === 'history'
    ? 'history'
    : 'jobs';
}

function writeCronViewToUrl(next: CronSubview) {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (next === 'jobs') params.delete(CRON_VIEW_PARAM);
  else params.set(CRON_VIEW_PARAM, next);
  const qs = params.toString();
  const newUrl = qs
    ? `${window.location.pathname}?${qs}${window.location.hash}`
    : `${window.location.pathname}${window.location.hash}`;
  window.history.pushState({}, '', newUrl);
}

interface CronJobsTabSectionProps {
  isActive: boolean;
}

const FETCH_TIMEOUT_MS = 25_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      window.setTimeout(() => reject(new Error('Request timed out. Click Refresh to try again.')), ms);
    }),
  ]);
}

function StatusBadge({ status }: { status: string | null | undefined }) {
  const label = status ?? 'unknown';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusChipClass(status)}`}>
      {label}
    </span>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${
        active ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-200 text-stone-600'
      }`}
      title={active ? 'Scheduled in pg_cron (not the same as a successful edge run)' : 'Paused — will not fire on schedule'}
    >
      {active ? 'Active' : 'Paused'}
    </span>
  );
}

// ── Configure drawer ────────────────────────────────────────────────────────

interface CronJobDrawerProps {
  job: CronJobRow;
  onClose: () => void;
  onUpdated: () => void;
}

function CronJobDrawer({ job, onClose, onUpdated }: CronJobDrawerProps) {
  const [runDays, setRunDays] = useState<number[]>(() => job.run_days ?? [0, 1, 2, 3, 4, 5, 6]);
  const [runHourUtc, setRunHourUtc] = useState<string>(
    () => (job.run_hour_utc != null ? String(job.run_hour_utc) : ''),
  );
  const [runMinuteUtc, setRunMinuteUtc] = useState<number>(job.run_minute_utc ?? 0);
  const [scheduleKind] = useState<'daily' | 'hourly' | 'every_n_hours'>(job.schedule_kind ?? 'daily');
  const [everyNHours] = useState<number | ''>(job.every_n_hours ?? '');
  const [maxBatch, setMaxBatch] = useState<string>(
    () => (job.max_batch != null ? String(job.max_batch) : ''),
  );
  const [active, setActive] = useState(job.active);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runStatus, setRunStatus] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const runAbortRef = useRef(false);

  useEffect(() => {
    // Stop any in-flight poll loop when the drawer closes / job changes.
    return () => {
      runAbortRef.current = true;
    };
  }, [job.jobid]);

  useEffect(() => {
    setRunDays(job.run_days ?? [0, 1, 2, 3, 4, 5, 6]);
    setRunHourUtc(job.run_hour_utc != null ? String(job.run_hour_utc) : '');
    setRunMinuteUtc(job.run_minute_utc ?? 0);
    setMaxBatch(job.max_batch != null ? String(job.max_batch) : '');
    setActive(job.active);
  }, [job.jobid, job.run_days, job.run_hour_utc, job.run_minute_utc, job.max_batch, job.active]);

  const toggleDay = (day: number) => {
    setRunDays((prev) => {
      const set = new Set(prev);
      if (set.has(day)) {
        if (set.size <= 1) return prev;
        set.delete(day);
      } else {
        set.add(day);
      }
      return [...set].sort((a, b) => a - b);
    });
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', h);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleSave = async () => {
    setErr(null);
    setMsg(null);
    if (runDays.length < 1) {
      setErr('Select at least one day of the week.');
      return;
    }
    setSaving(true);
    try {
      const hour =
        scheduleKind === 'daily' && runHourUtc.trim() !== ''
          ? Math.min(23, Math.max(0, parseInt(runHourUtc, 10)))
          : null;
      const batchParsed =
        maxBatch.trim() === '' ? null : Math.min(500, Math.max(1, parseInt(maxBatch, 10)));
      const newSchedule = await updateCronJobSettings(job.jobname, {
        run_days: runDays,
        run_hour_utc: hour,
        run_minute_utc: Math.min(59, Math.max(0, runMinuteUtc)),
        schedule_kind: scheduleKind,
        every_n_hours: scheduleKind === 'every_n_hours' && everyNHours !== '' ? Number(everyNHours) : null,
        max_batch: Number.isFinite(batchParsed as number) ? batchParsed : null,
        active,
      });
      setMsg(`Saved. pg_cron schedule → ${newSchedule || describeCronSchedule(job.schedule, job)}`);
      onUpdated();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    setErr(null);
    setMsg(null);
    setRunStatus('Sending trigger to the server…');
    setRunning(true);
    runAbortRef.current = false;
    const hint = runNowResultHint(job.jobname);
    try {
      // Use direct edge-function invocation for all jobs (same pattern as peerlist).
      // Falls back to peerlist's own RPC for peerlist jobs (already proven path).
      const isPeerlist = job.jobname === 'peerlist-ingest-every-6h'
        || job.target_function === 'peerlist-ingest';
      const requestId = isPeerlist
        ? await invokePeerlistIngest()
        : await invokeCronJobDirect(job.jobname);

      if (!requestId) {
        // No pg_net request id to track — fall back to the optimistic message.
        setMsg(`Triggered. ${hint}`);
        window.setTimeout(() => onUpdated(), 4000);
        return;
      }

      // Record a pending run immediately so Execution History shows it as "running".
      await beginCronInvoke(requestId, job.jobname, job.target_function);
      // Reveal the new pending row in Execution History right away.
      onUpdated();

      // Poll the real async HTTP result so the UI reflects what the backend did.
      setRunStatus(`Running in the background… (request #${requestId})`);
      const startedAt = Date.now();
      const TIMEOUT_MS = 90_000;
      const INTERVAL_MS = 2500;
      let resolved = false;

      while (Date.now() - startedAt < TIMEOUT_MS) {
        await new Promise((r) => window.setTimeout(r, INTERVAL_MS));
        if (runAbortRef.current) return;
        const secs = Math.round((Date.now() - startedAt) / 1000);
        setRunStatus(`Running in the background… ${secs}s elapsed (request #${requestId})`);

        const result = await pollCronInvoke(requestId, job.jobname).catch(() => null);
        if (runAbortRef.current) return;
        if (result?.done) {
          resolved = true;
          if (result.ok) {
            if (result.phase === 'poll_unavailable') {
              const resolvedRun = await resolveEdgeRunMessage(requestId, hint);
              if (resolvedRun?.ok) setMsg(resolvedRun.msg ?? null);
              else if (resolvedRun?.err) setErr(resolvedRun.err);
              else {
                setMsg(
                  `Triggered (request #${requestId}) — job is running; live status polling is unavailable. ${hint}`,
                );
              }
            } else if (result.phase === 'poll_access_denied') {
              setMsg(
                `Triggered (request #${requestId}) — could not poll status (access denied). ${hint}`,
              );
            } else {
              const preview = (result.responsePreview ?? '').toString().slice(0, 160);
              setMsg(`✓ Completed — HTTP ${result.httpStatus ?? 200}.${preview ? ` ${preview}` : ''} ${hint}`);
            }
          } else {
            const detail = (result.errorMessage || result.responsePreview || '').toString().slice(0, 200);
            setErr(
              `Job ran but reported a problem — HTTP ${result.httpStatus ?? '—'}`
              + (detail ? ` · ${detail}` : '')
              + '. See Execution History for details.',
            );
          }
          break;
        }
      }

      if (!resolved && !runAbortRef.current) {
        const resolvedRun = await resolveEdgeRunMessage(requestId, hint);
        if (resolvedRun?.ok) setMsg(resolvedRun.msg ?? null);
        else if (resolvedRun?.err) setErr(resolvedRun.err);
        else {
          setMsg(`Triggered (request #${requestId}) — still running after 90s (long job). ${hint}`);
        }
      }

      // Refresh jobs + history so the new run/result is visible immediately.
      onUpdated();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setRunStatus(null);
      setRunning(false);
    }
  };

  return createPortal(
    <>
      <div aria-hidden className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      <aside
        role="dialog"
        aria-modal
        aria-label={`Configure ${job.jobname}`}
        className="fixed right-0 top-0 bottom-0 z-50 flex flex-col w-[400px] sm:w-[480px] shadow-2xl overflow-hidden"
        style={{
          background: 'var(--surface-card-elevated, var(--vscode-bg-elevated))',
          borderLeft: '1px solid var(--vscode-border-primary)',
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0" style={{ borderColor: 'var(--vscode-border-secondary)' }}>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: 'var(--vscode-text-secondary)' }}>Cron job</p>
            <h2 className="text-lg font-bold truncate">{job.jobname}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          <div>
            <p className="text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>Category</p>
            <p className="text-[14px]">{job.category}</p>
          </div>

          <div>
            <p className="text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>Target</p>
            <p className="text-[14px] font-mono">{job.target_function}</p>
          </div>

          {job.is_placeholder && (
            <p className="text-[13px] text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
              This job is still a placeholder. Apply migration <strong>20260602240021</strong> or run <code>scripts/configure-peerlist-ingest.sql</code> in the SQL editor.
            </p>
          )}

          {job.has_settings === false && (
            <p className="text-[13px] text-amber-800 bg-amber-50 rounded-lg px-3 py-2">
              Schedule below is read from the live <strong>pg_cron</strong> expression — no saved settings row yet.
              Click <strong>Save</strong> to persist your choices and sync pg_cron.
            </p>
          )}

          {(job.jobname.includes('blog') || job.jobname.includes('draft')) && (
            <p className="text-[13px] text-sky-900 bg-sky-50 rounded-lg px-3 py-2">
              <strong>Content / SEO</strong> — cron generates <strong>drafts only</strong>. Review in{' '}
              <strong>Admin → Blog posts</strong> and publish manually when ready.
              pg_cron <code className="text-[11px]">succeeded</code> only means the HTTP call was queued — check{' '}
              <code className="text-[11px]">blog_keyword_queue</code> and <code className="text-[11px]">blog_posts</code> for outcomes.
            </p>
          )}

          <div>
            <p className="text-[12px] font-medium mb-2" style={{ color: 'var(--vscode-text-secondary)' }}>
              Run on (UTC weekdays)
            </p>
            <div className="flex flex-wrap gap-1.5">
              {CRON_WEEKDAYS.map(({ value, short }) => {
                const on = runDays.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleDay(value)}
                    className={`px-2.5 py-1 rounded-md text-[12px] font-semibold border transition-colors ${
                      on
                        ? 'bg-emerald-600 text-white border-emerald-600'
                        : 'border-stone-300 text-stone-600 hover:bg-stone-50'
                    }`}
                    aria-pressed={on}
                  >
                    {short}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] mt-1.5" style={{ color: 'var(--vscode-text-secondary)' }}>
              {formatRunDays(runDays)} · edge functions skip sends on unselected days
            </p>
          </div>

          {scheduleKind === 'daily' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                  Hour (UTC)
                </label>
                <select
                  value={runHourUtc}
                  onChange={(e) => setRunHourUtc(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border text-[13px]"
                  style={{ borderColor: 'var(--vscode-border-secondary)', background: 'var(--vscode-bg)' }}
                >
                  <option value="">—</option>
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={String(h)}>
                      {String(h).padStart(2, '0')}:00 UTC
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                  Minute
                </label>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={runMinuteUtc}
                  onChange={(e) => setRunMinuteUtc(Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)))}
                  className="w-full px-3 py-2 rounded-lg border text-[13px]"
                  style={{ borderColor: 'var(--vscode-border-secondary)', background: 'var(--vscode-bg)' }}
                />
              </div>
            </div>
          )}

          {scheduleKind === 'hourly' && (
            <p className="text-[13px] rounded-lg px-3 py-2 bg-stone-100 text-stone-700">
              Runs every hour on selected days (at minute {runMinuteUtc} past each hour).
            </p>
          )}

          {scheduleKind === 'every_n_hours' && (
            <p className="text-[13px] rounded-lg px-3 py-2 bg-stone-100 text-stone-700">
              Every {everyNHours || '—'} hours at minute {runMinuteUtc} on selected days.
            </p>
          )}

          {job.supports_max_batch && (
            <div>
              <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                Max emails per run
                {job.jobname.includes('outreach') ? ' (outreach)' : ''}
              </label>
              <input
                type="number"
                min={1}
                max={500}
                value={maxBatch}
                onChange={(e) => setMaxBatch(e.target.value)}
                placeholder="e.g. 50"
                className="w-full px-3 py-2 rounded-lg border text-[13px]"
                style={{ borderColor: 'var(--vscode-border-secondary)', background: 'var(--vscode-bg)' }}
              />
              <p className="text-[11px] mt-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                Caps Resend sends for this job. Helps stay under your daily quota (100 on free tier).
              </p>
            </div>
          )}

          <div>
            <p className="text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>
              pg_cron schedule (auto-synced on save)
            </p>
            <p className="text-[13px] font-mono">{job.schedule}</p>
            <p className="text-[11px] mt-1" style={{ color: 'var(--vscode-text-secondary)' }}>
              {describeCronSchedule(job.schedule, job)}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-[12px] font-medium" style={{ color: 'var(--vscode-text-secondary)' }}>Status</p>
              <ActiveBadge active={active} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <span className="text-[13px]">Active (scheduled)</span>
              <input
                type="checkbox"
                checked={active}
                onChange={e => setActive(e.target.checked)}
                className="w-4 h-4 accent-emerald-600"
              />
            </label>
          </div>

          <div>
            <p className="text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>Last run</p>
            <p className="text-[13px]">{formatTimestamp(job.last_run_at)}</p>
            {job.last_status && <div className="mt-1"><StatusBadge status={job.last_status} /></div>}
          </div>

          <div>
            <p className="text-[12px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>Command</p>
            <pre
              className="text-[11px] p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all max-h-40"
              style={{ background: 'var(--vscode-bg)', color: 'var(--vscode-text-secondary)' }}
            >
              {job.command}
            </pre>
          </div>

          {running && runStatus && (
            <div className="flex items-center gap-2 text-[13px] text-blue-800 bg-blue-50 rounded-lg px-3 py-2">
              <LoadingSpinner size="sm" />
              <span>{runStatus}</span>
            </div>
          )}
          {msg && <p className="text-[13px] text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">{msg}</p>}
          {err && <p className="text-[13px] text-red-700 bg-red-50 rounded-lg px-3 py-2">{err}</p>}
        </div>

        <div className="shrink-0 px-5 py-4 border-t flex flex-wrap gap-2" style={{ borderColor: 'var(--vscode-border-secondary)' }}>
          <button
            type="button"
            onClick={() => void handleRun()}
            disabled={running || saving}
            className="exa-toolbar-btn exa-toolbar-btn-accent inline-flex items-center gap-1.5"
          >
            {running ? <LoadingSpinner size="sm" /> : <Play className="w-3.5 h-3.5" />}
            {running ? 'Running…' : 'Run now'}
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || running}
            className="exa-toolbar-btn inline-flex items-center gap-1.5"
          >
            {saving ? <LoadingSpinner size="sm" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </aside>
    </>,
    document.body,
  );
}

// ── Main section ────────────────────────────────────────────────────────────

export default function CronJobsTabSection({ isActive = true }: CronJobsTabSectionProps) {
  const [subview, setSubview] = useState<CronSubview>(() => readCronViewFromUrl());
  const [jobs, setJobs] = useState<CronJobRow[]>([]);
  const [runs, setRuns] = useState<CronJobRunRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<CronJobRow | null>(null);
  const [selectedRun, setSelectedRun] = useState<CronJobRunRow | null>(null);
  const hasLoadedRef = useRef(false);
  const historyLoadedRef = useRef(false);
  const jobsLoadGenRef = useRef(0);
  const historyLoadGenRef = useRef(0);

  const loadJobs = useCallback(async (opts?: { silent?: boolean }) => {
    const gen = ++jobsLoadGenRef.current;
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const data = await withTimeout(fetchCronJobs(), FETCH_TIMEOUT_MS);
      if (gen !== jobsLoadGenRef.current) return;
      setJobs(data);
    } catch (e) {
      if (gen !== jobsLoadGenRef.current) return;
      const msg = (e as Error).message;
      setError(msg.includes('forbidden') || msg.includes('Access denied')
        ? `${msg} — cron jobs exist in the database but your session cannot read them. Try signing out and back in.`
        : msg);
      setJobs([]);
    } finally {
      if (gen === jobsLoadGenRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const loadHistory = useCallback(async (opts?: { silent?: boolean }) => {
    const gen = ++historyLoadGenRef.current;
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const data = await withTimeout(fetchMergedExecutionHistory(200), FETCH_TIMEOUT_MS);
      if (gen !== historyLoadGenRef.current) return;
      setRuns(data);
    } catch (e) {
      if (gen !== historyLoadGenRef.current) return;
      setError((e as Error).message);
    } finally {
      if (gen === historyLoadGenRef.current) {
        setLoading(false);
      }
    }
  }, []);

  const selectSubview = useCallback((next: CronSubview) => {
    setSubview(next);
    writeCronViewToUrl(next);
    if (next === 'history' && runs.length === 0 && !loading) {
      void loadHistory({ silent: false });
    }
  }, [loadHistory, loading, runs.length]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => setSubview(readCronViewFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (isActive) setSubview(readCronViewFromUrl());
  }, [isActive]);

  useEffect(() => {
    if (!isActive) return;
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      void loadJobs();
    }
    if (!historyLoadedRef.current) {
      historyLoadedRef.current = true;
      void loadHistory({ silent: true });
    }
  }, [isActive, loadJobs, loadHistory]);

  const refresh = useCallback(() => {
    if (subview === 'jobs') void loadJobs({ silent: jobs.length > 0 });
    else void loadHistory({ silent: runs.length > 0 });
  }, [subview, loadJobs, loadHistory, jobs.length, runs.length]);

  const missingJobs = useMemo(() => {
    const names = new Set(jobs.map(j => j.jobname));
    return EXPECTED_CRON_JOBS.filter(n => !names.has(n));
  }, [jobs]);

  const staleAuthJobs = useMemo(
    () => jobs.filter(j => j.command.includes("'anon_key'")),
    [jobs],
  );

  const jobCols = useMemo<Column<CronJobRow>[]>(() => [
    {
      key: 'category',
      header: 'Category',
      minWidth: 100,
      cell: v => (
        <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-600">
          {String(v)}
        </span>
      ),
    },
    {
      key: 'jobname',
      header: 'Job name',
      minWidth: 180,
      cell: (v, row) => (
        <div>
          <span className="font-medium">{String(v)}</span>
          {row.is_placeholder && (
            <p className="text-[10px] text-amber-700 mt-0.5">Placeholder — run migration or configure script</p>
          )}
        </div>
      ),
    },
    {
      key: 'active',
      header: 'State',
      minWidth: 80,
      cell: v => <ActiveBadge active={Boolean(v)} />,
    },
    {
      key: 'last_run_at',
      header: 'Last run',
      minWidth: 130,
      cell: v => <span className="text-[12px]">{formatTimestamp(v as string | null)}</span>,
    },
    {
      key: 'last_status',
      header: 'Last status',
      minWidth: 100,
      cell: v => v ? <StatusBadge status={String(v)} /> : <span className="text-[12px] opacity-50">—</span>,
    },
    {
      key: 'schedule',
      header: 'Schedule',
      minWidth: 120,
      cell: (v, row) => (
        <div className="whitespace-normal">
          <span className="font-mono text-[12px]">{String(v)}</span>
          <p className="text-[10px] mt-0.5 opacity-70">
            {describeCronSchedule(String(v), row)}
          </p>
        </div>
      ),
    },
    {
      key: 'target_function',
      header: 'Target',
      minWidth: 140,
      defaultHidden: true,
      cell: v => <span className="font-mono text-[12px]">{String(v)}</span>,
    },
  ], []);

  const runCols = useMemo<Column<CronJobRunRow>[]>(() => [
    {
      key: 'jobname',
      header: 'Job',
      minWidth: 180,
      cell: v => <span className="font-medium">{String(v ?? '—')}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      minWidth: 100,
      cell: v => <StatusBadge status={String(v)} />,
    },
    {
      key: 'start_time',
      header: 'Started',
      minWidth: 160,
      cell: v => <span className="text-[12px]">{formatTimestamp(String(v))}</span>,
    },
    {
      key: 'end_time',
      header: 'Ended',
      minWidth: 160,
      defaultHidden: true,
      cell: v => <span className="text-[12px]">{formatTimestamp(v as string | null)}</span>,
    },
    {
      key: 'duration_ms',
      header: 'Duration',
      minWidth: 90,
      cell: v => <span className="text-[12px] font-mono">{formatDuration(v as number | null)}</span>,
    },
    {
      key: 'return_message',
      header: 'Message',
      minWidth: 220,
      cell: v => {
        const s = String(v ?? '');
        return (
          <span className="text-[12px] truncate block max-w-[280px]" title={s}>
            {s || '—'}
          </span>
        );
      },
    },
  ], []);

  const runningCount = runs.filter(r => {
    const s = r.status.toLowerCase();
    return s === 'running' || s === 'started';
  }).length;

  return (
    <>
      <div className="flex gap-1 mb-3 border-b" style={{ borderColor: 'var(--vscode-border-secondary)' }}>
        {([
          { id: 'jobs' as const, label: 'Cron Jobs' },
          { id: 'history' as const, label: 'Execution History' },
        ]).map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => selectSubview(tab.id)}
            className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
              subview === tab.id ? 'border-purple-600 text-purple-600' : 'border-transparent'
            }`}
            style={{ color: subview === tab.id ? undefined : 'var(--vscode-text-secondary)' }}
          >
            {tab.label}
            {tab.id === 'history' && runs.length > 0 && (
              <span className="ml-1.5 text-[10px] opacity-70">({runs.length})</span>
            )}
          </button>
        ))}
      </div>

      <div className="ds-cp-toolbar flex flex-wrap items-center gap-2 mb-3">
        <span className="ds-cp-toolbar-count">
          {subview === 'jobs'
            ? `${jobs.length} scheduled job${jobs.length === 1 ? '' : 's'}`
            : `${runs.length} run${runs.length === 1 ? '' : 's'}${runningCount > 0 ? ` · ${runningCount} running` : ''}`}
        </span>
        <div className="flex-1" />
        <button type="button" onClick={refresh} className="exa-toolbar-btn" disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="ds-cp-error rounded-xl px-3 py-2 text-[13px] mb-3">{error}</div>
      )}

      {subview === 'jobs' && missingJobs.length > 0 && (
        <div className="mb-3 rounded-xl px-4 py-3 text-[13px] bg-amber-50 text-amber-900 border border-amber-200">
          <p className="font-semibold mb-1">Missing cron jobs ({missingJobs.length})</p>
          <p className="mb-2">Run <code className="text-[11px]">supabase db push</code> (migration <strong>20260609120036</strong>) so these register in pg_cron:</p>
          <p className="font-mono text-[11px]">{missingJobs.join(', ')}</p>
        </div>
      )}

      {subview === 'jobs' && staleAuthJobs.length > 0 && (
        <div className="mb-3 rounded-xl px-4 py-3 text-[13px] bg-red-50 text-red-900 border border-red-200">
          <p className="font-semibold mb-1">Stale Vault auth on {staleAuthJobs.length} job(s)</p>
          <p>These jobs use <code className="text-[11px]">anon_key</code> and may fail silently. Apply migration <strong>20260609120036</strong> to switch them to <code className="text-[11px]">service_role_key</code>.</p>
        </div>
      )}

      {subview === 'jobs' && (
        <details className="ds-cp-cron-help mb-3 rounded-xl border border-violet-200 bg-violet-50 text-violet-950">
          <summary className="cursor-pointer select-none px-4 py-2.5 text-[12px] font-semibold">
            Blog pipeline (Content / SEO) — tips
          </summary>
          <p className="px-4 pb-3 text-[12px] leading-relaxed">
            Cron generates <strong>drafts only</strong> — admins review in <strong>Admin → Blog posts</strong> and publish manually.
            Default schedule is <strong>Mon–Fri 06:30 UTC</strong>. Do <strong>not</strong> set <code className="text-[11px]">BLOG_AUTO_PUBLISH</code> in Supabase secrets.
            If runs show succeeded but no new drafts, check keywords stuck in <code className="text-[11px]">generating</code> in <code className="text-[11px]">blog_keyword_queue</code>.
          </p>
        </details>
      )}

      {subview === 'jobs' && (
        <details className="ds-cp-cron-help mb-3 rounded-xl border border-sky-200 bg-sky-50 text-sky-950">
          <summary className="cursor-pointer select-none px-4 py-2.5 text-[12px] font-semibold">
            State vs execution — how to read this table
          </summary>
          <p className="px-4 pb-3 text-[12px] leading-relaxed">
            <strong>Active</strong> means the job is enabled in pg_cron — not that the last edge function run succeeded.
            Check <strong>Last run</strong> and <strong>Last status</strong> columns; pg_cron often reports <code className="text-[11px]">succeeded</code> with message <code className="text-[11px]">1 row</code> when it only queued an HTTP call.
            Use <strong>Execution History</strong> and outcome tables (<code className="text-[11px]">ingestion_logs</code>, <code className="text-[11px]">blog_posts</code>, <code className="text-[11px]">outreach_touchpoints</code>) to confirm real results.
          </p>
        </details>
      )}

      {subview === 'jobs' && (
        <details
          className="ds-cp-cron-help mb-3 rounded-xl border text-[12px] leading-relaxed"
          style={{
            borderColor: 'var(--vscode-border-secondary)',
            background: 'rgba(21,128,61,0.06)',
            color: 'var(--vscode-text-secondary)',
          }}
        >
          <summary
            className="cursor-pointer select-none px-4 py-2.5 font-semibold"
            style={{ color: 'var(--vscode-text-primary)' }}
          >
            Internal DB vs external fetch
          </summary>
          <div className="px-4 pb-3 space-y-1">
            <p><strong>Internal DB</strong> — jobs already stored in <code className="text-[11px]">peerlist_jobs</code> (all sources: Peerlist, LinkedIn, Naukri).</p>
            <p><strong>External fetch</strong> — live pull via Refresh (Jobs tab) or <code className="text-[11px]">refresh-job-feeds</code>.</p>
            <p><strong>Job feeds cron</strong> — <code className="text-[11px]">peerlist-ingest-every-6h</code> runs automatically; check Execution History for <code className="text-[11px]">peerlist-ingest</code> in <code className="text-[11px]">ingestion_logs</code> if pg_cron shows succeeded but no new jobs appear.</p>
            <p><strong>Manual only</strong> — Find Jobs and Find Leads are on-demand, not pg_cron jobs.</p>
          </div>
        </details>
      )}

      {subview === 'jobs' ? (
        <DataTable<CronJobRow>
          columns={jobCols}
          rows={jobs}
          loading={loading}
          initialPageSize={50}
          onRowClick={row => setSelectedJob(row)}
          emptyMessage={loading ? 'Loading cron jobs…' : 'No cron jobs found on this Supabase project.'}
          className="ds-cp-cron-jobs-table"
          ariaLabel="Scheduled cron jobs"
        />
      ) : (
        <>
          <div
            className="mb-3 rounded-xl px-4 py-3 text-[12px] leading-relaxed border"
            style={{
              borderColor: 'var(--vscode-border-secondary)',
              background: 'rgba(59,130,246,0.06)',
              color: 'var(--vscode-text-secondary)',
            }}
          >
            <p>
              <strong>Peerlist ingest results</strong> live in the Postgres table{' '}
              <code className="text-[11px]">public.ingestion_logs</code> — query it in the{' '}
              <strong>SQL Editor</strong>, not Logs &amp; Analytics (that UI only has edge/postgres log streams, not app tables).
            </p>
          </div>
          <div className="flex gap-0 min-h-[420px] border rounded-xl overflow-hidden" style={{ borderColor: 'var(--vscode-border-secondary)' }}>
          <div className={`min-w-0 transition-all ${selectedRun ? 'w-[55%] lg:w-[60%]' : 'w-full'}`}>
            <DataTable<CronJobRunRow>
              columns={runCols}
              rows={runs}
              loading={loading}
              initialPageSize={50}
              onRowClick={row => setSelectedRun(row)}
              emptyMessage={loading ? 'Loading execution history…' : 'No execution history yet.'}
            />
          </div>

          {selectedRun && (
            <aside
              className="w-[45%] lg:w-[40%] border-l flex flex-col min-h-0"
              style={{
                borderColor: 'var(--vscode-border-secondary)',
                background: 'var(--surface-card-elevated, var(--vscode-bg-elevated))',
              }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b shrink-0" style={{ borderColor: 'var(--vscode-border-secondary)' }}>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wide font-semibold" style={{ color: 'var(--vscode-text-secondary)' }}>Run log</p>
                  <h3 className="text-[14px] font-bold truncate">{selectedRun.jobname ?? `Job #${selectedRun.jobid}`}</h3>
                </div>
                <button type="button" onClick={() => setSelectedRun(null)} className="p-1 rounded hover:bg-black/5" aria-label="Close panel">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4 text-[13px]">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] font-medium mb-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>Run ID</p>
                    <p className="font-mono">{selectedRun.runid}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium mb-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>Status</p>
                    <StatusBadge status={selectedRun.status} />
                  </div>
                  <div>
                    <p className="text-[11px] font-medium mb-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>Started</p>
                    <p>{formatTimestamp(selectedRun.start_time)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium mb-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>Ended</p>
                    <p>{formatTimestamp(selectedRun.end_time)}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium mb-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>Duration</p>
                    <p className="font-mono">{formatDuration(selectedRun.duration_ms)}</p>
                  </div>
                </div>

                <div>
                  <p className="text-[11px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>Return message</p>
                  <pre
                    className="text-[12px] p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all"
                    style={{ background: 'var(--vscode-bg)', color: 'var(--vscode-text-primary)' }}
                  >
                    {selectedRun.return_message?.trim() || '(no message)'}
                  </pre>
                </div>

                <div>
                  <p className="text-[11px] font-medium mb-1" style={{ color: 'var(--vscode-text-secondary)' }}>Command executed</p>
                  <pre
                    className="text-[11px] p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all max-h-48"
                    style={{ background: 'var(--vscode-bg)', color: 'var(--vscode-text-secondary)' }}
                  >
                    {selectedRun.command}
                  </pre>
                </div>
              </div>
            </aside>
          )}
        </div>
        </>
      )}

      {selectedJob && (
        <CronJobDrawer
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          onUpdated={() => {
            void loadJobs({ silent: true });
            // Always reload history after Run Now / Save so results appear without manual refresh
            historyLoadedRef.current = false;
            void loadHistory({ silent: true });
          }}
        />
      )}
    </>
  );
}

export { CronJobsTabSection };
